import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Icon } from '../../components/Icon.js';
import { useSubjectSearch } from './quotes-ui.js';

const SHOWN = 8;

/**
 * Choosing the client of a quote (proposal §4): one box where you write the
 * name or the VAT number, the matches appear underneath, and when nothing
 * matches the box offers to enter the subject in Anagrafica and comes back
 * here with it already chosen.
 */
export function ClientPicker({
  value,
  onChange,
  label = 'Cliente',
  role = 'cliente',
  error,
  hint,
  /** Where to come back to after entering a new subject. */
  returnTo,
}: {
  value: string | null;
  onChange: (clientId: string | null) => void;
  label?: string;
  /** The role the subject should have: Cliente for a quote, Fornitore for a received document (1.59). */
  role?: 'cliente' | 'fornitore';
  error?: string;
  hint?: string;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const canRegistry = useCan('anagrafica.read');
  const canWriteRegistry = useCan('anagrafica.write');
  const subjects = useSubjectSearch(canRegistry);
  const [query, setQuery] = useState('');

  if (!canRegistry) {
    return (
      <p className="notice info">
        Il tuo profilo non vede l’Anagrafica: puoi preparare la bozza, il cliente lo sceglie chi ha
        accesso ai soggetti.
      </p>
    );
  }

  const all = subjects.data ?? [];
  const supplier = role === 'fornitore';
  const hasRole = (subject: (typeof all)[number]) =>
    supplier ? subject.isSupplier : subject.isClient;
  const chosen = all.find((subject) => subject.id === value);
  const needle = query.trim().toLowerCase();
  const searching = needle.length >= 2;
  const found = searching ? all.filter((subject) => subject.search.includes(needle)) : [];

  // The field is not a <label>: it holds buttons and a list, not one control.
  return (
    <div className={error ? 'field has-error' : 'field'}>
      <span className="field-label">{label}</span>
      {chosen ? (
        <div className="chosen-client">
          <div>
            <strong>{chosen.name}</strong>
            <small className="muted block">
              {[chosen.vatNumber ?? chosen.taxCode, chosen.city].filter(Boolean).join(' · ') ||
                'Nessun altro dato'}
            </small>
            {!hasRole(chosen) && (
              <small className="block warn-text">
                Non ha ancora il ruolo {supplier ? 'Fornitore' : 'Cliente'}: aggiungilo dalla sua
                scheda in Anagrafica.
              </small>
            )}
            {!supplier && chosen.isClient && !chosen.usable && (
              <small className="block warn-text">
                Da completare o disattivato: senza partita IVA o codice fiscale non si può emettere.
              </small>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
            }}
          >
            Cambia
          </button>
        </div>
      ) : (
        <>
          <label className="search-box">
            <Icon name="search" />
            <input
              value={query}
              placeholder="Denominazione o partita IVA"
              autoComplete="off"
              aria-label={`${label}: cerca per denominazione o partita IVA`}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {searching && found.length > 0 && (
            <ul className="search-results">
              {found.slice(0, SHOWN).map((subject) => (
                <li key={subject.id}>
                  <button type="button" onClick={() => onChange(subject.id)}>
                    <strong>{subject.name}</strong>
                    <small className="muted">
                      {[subject.vatNumber ?? subject.taxCode, subject.city]
                        .filter(Boolean)
                        .join(' · ')}
                      {hasRole(subject)
                        ? ''
                        : supplier
                          ? ' · non è ancora un fornitore'
                          : ' · non è ancora un cliente'}
                      {supplier || !subject.isClient || subject.usable ? '' : ' · da completare'}
                    </small>
                  </button>
                </li>
              ))}
              {found.length > SHOWN && (
                <li className="muted small">
                  e altri {found.length - SHOWN}: scrivi qualche lettera in più.
                </li>
              )}
            </ul>
          )}
          {searching && found.length === 0 && (
            <div className="notice no-match">
              <span>
                <strong>Nessun riscontro in anagrafica</strong> per «{query.trim()}».
                {canWriteRegistry
                  ? ' Vuoi inserirla?'
                  : ' Chiedi a chi può inserire le anagrafiche.'}
              </span>
              {canWriteRegistry && (
                <button
                  type="button"
                  className="small primary"
                  onClick={() =>
                    navigate(
                      `/anagrafica/nuovo?name=${encodeURIComponent(query.trim())}${supplier ? '&ruolo=F' : ''}&ritorno=${encodeURIComponent(returnTo)}`,
                    )
                  }
                >
                  Inserisci in Anagrafica
                </button>
              )}
            </div>
          )}
        </>
      )}
      {hint && !error && <small>{hint}</small>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
